(() => {
  if (customElements.get('sentopia-set-gallery')) return;
  const saved = new WeakMap();
  class SentopiaSetGallery extends HTMLElement {
    connectedCallback() {
      const root = this.closest('#sentopia-proposals');
      if (!root || this.controller) return;
      const config = JSON.parse(root.querySelector('[data-gift-config]').textContent);
      this.photos = config.gallery?.photos?.filter(photo => photo.src) || [];
      if (!this.photos.length) return;
      this.labels = config.gallery.labels;
      this.root = root;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.state = saved.get(root) || { index: 0, paused: this.motion.matches };
      saved.set(root, this.state);
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      const esc = value => String(value || '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', this.labels.carousel);
      this.setAttribute('aria-label', this.labels.name);
      this.innerHTML = `<div class="sp-gallery-heading"><span>${esc(config.photoEyebrow)}</span><h3>${esc(config.photoHeading)}</h3></div><div class="sp-gallery-stage">${this.photos.map((photo, index) => `<div class="sp-gallery-slide" data-gallery-slide="${index}" role="group" aria-roledescription="${esc(this.labels.slide)}" aria-label="${index + 1} / ${this.photos.length}"><img src="${esc(photo.src)}" width="${photo.width}" height="${photo.height}" loading="lazy" decoding="async" alt="${esc(photo.alt)}"></div>`).join('')}</div><div class="sp-gallery-controls"><button type="button" data-gallery-prev aria-label="${esc(this.labels.previous)}"><span aria-hidden="true">‹</span></button><div class="sp-gallery-dots">${this.photos.map((_, index) => `<button type="button" data-gallery-dot="${index}" aria-label="${esc(this.labels.photo)} ${index + 1}" aria-pressed="false"><span aria-hidden="true"></span></button>`).join('')}</div><button type="button" data-gallery-next aria-label="${esc(this.labels.next)}"><span aria-hidden="true">›</span></button><button type="button" data-gallery-pause></button></div><span class="sp-gallery-status" aria-live="polite" aria-atomic="true"></span>`;
      this.stage = this.querySelector('.sp-gallery-stage');
      this.stage.tabIndex = 0;
      this.stage.setAttribute('aria-label', this.labels.name);
      this.stage.querySelectorAll('img').forEach(image => { image.draggable = false; });
      this.stage.addEventListener('scroll', () => {
        this.state.index = this.nearest();
        this.update();
        this.schedule();
      }, { ...options, passive: true });
      this.stage.addEventListener('pointerdown', event => {
        this.pointerDown = true;
        this.focused = false;
        clearTimeout(this.timer);
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        event.preventDefault();
        this.drag = { x: event.clientX, left: this.stage.scrollLeft };
        this.stage.classList.add('is-dragging');
        this.stage.setPointerCapture(event.pointerId);
      }, options);
      this.stage.addEventListener('pointermove', event => {
        if (this.drag) this.stage.scrollLeft = this.drag.left + this.drag.x - event.clientX;
      }, options);
      const finishDrag = event => {
        this.pointerDown = false;
        if (this.drag) {
          this.drag = null;
          this.stage.classList.remove('is-dragging');
          if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
          this.show(this.nearest(), true);
        }
        this.schedule();
      };
      this.stage.addEventListener('pointerup', finishDrag, options);
      this.stage.addEventListener('pointercancel', finishDrag, options);
      this.resize = new ResizeObserver(() => this.position(false));
      this.resize.observe(this.stage);
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-gallery-pause')) {
          this.state.paused = !this.state.paused;
          if (!this.state.paused) this.focused = false;
          this.update();
          this.schedule();
        } else if (button.hasAttribute('data-gallery-prev')) this.show(this.state.index - 1, true);
        else if (button.hasAttribute('data-gallery-next')) this.show(this.state.index + 1, true);
        else if (button.hasAttribute('data-gallery-dot')) this.show(Number(button.dataset.galleryDot), true);
      }, options);
      // Mouse/touch interaction restarts the interval; keyboard focus still pauses rotation.
      this.addEventListener('pointerdown', () => { this.focused = false; this.schedule(); }, options);
      this.addEventListener('focusin', event => { this.focused = event.target.matches(':focus-visible'); this.schedule(); }, options);
      this.addEventListener('focusout', event => { this.focused = Boolean(this.contains(event.relatedTarget) && event.relatedTarget.matches(':focus-visible')); this.schedule(); }, options);
      this.addEventListener('keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.show(this.state.index + (event.key === 'ArrowRight' ? 1 : -1), true);
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.state.paused = true;
        this.update();
        this.schedule();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        this.schedule();
      }, { threshold: 0.15 });
      this.observer.observe(this);
      this.update();
    }
    show(index, announce = false) {
      this.state.index = (index + this.photos.length) % this.photos.length;
      this.update();
      this.position(!this.motion.matches);
      if (announce) this.querySelector('.sp-gallery-status').textContent = `${this.labels.photo} ${this.state.index + 1} / ${this.photos.length}`;
      this.schedule();
    }
    nearest() {
      const slides = [...this.stage.children];
      const origin = this.stage.getBoundingClientRect().left;
      return slides.reduce((best, slide, index) => Math.abs(slide.getBoundingClientRect().left - origin) < Math.abs(slides[best].getBoundingClientRect().left - origin) ? index : best, 0);
    }
    position(smooth) {
      const slides = this.stage.children;
      if (!slides[this.state.index]) return;
      const left = this.stage.scrollLeft + slides[this.state.index].getBoundingClientRect().left - this.stage.getBoundingClientRect().left;
      this.stage.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
    }
    update() {
      this.querySelectorAll('[data-gallery-slide]').forEach((slide, index) => {
        const active = index === this.state.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.querySelectorAll('[data-gallery-dot]').forEach((button, index) => button.setAttribute('aria-pressed', String(index === this.state.index)));
      const pause = this.querySelector('[data-gallery-pause]');
      pause.setAttribute('aria-label', this.state.paused ? this.labels.play : this.labels.pause);
      pause.textContent = this.state.paused ? '▶' : 'Ⅱ';
      this.dataset.galleryIndex = String(this.state.index);
      this.dataset.galleryPaused = String(this.state.paused);
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.visible || this.focused || this.pointerDown || this.state.paused || document.hidden || this.photos.length < 2) return;
      this.timer = setTimeout(() => this.show(this.state.index + 1), 5000);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.resize?.disconnect();
      this.observer?.disconnect();
      this.controller?.abort();
      this.controller = null;
    }
  }
  customElements.define('sentopia-set-gallery', SentopiaSetGallery);
})();
